import { computed, ref } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { deepCopy } from "@utils/toolBox";
import handleError from "@composables/handleError";
import useMyFetch from "@stores/useMyFetch";
import { useMCampaignStore } from "@stores/mCampaign";
import { useMCreativeStore } from "@stores/mCreative";
import { useMMediaStore } from "@stores/mMedia";
import genToken from "@utils/generateToken";

export const useMCreativesStore = defineStore("mCreatives", () => {
  const mCampaignStore = useMCampaignStore();
  const { currentMCampaign, initialMCampaign } = storeToRefs(mCampaignStore);

  const _selectedMCreativeIndex = ref(0);

  const creatives = computed(() => currentMCampaign.value.m_creatives);
  const initialCreatives = computed(() => initialMCampaign.value.m_creatives);
  const selectedMCreative = computed(() => creatives.value.at(_selectedMCreativeIndex.value));

  const selectCreative = (index) => {
    _selectedMCreativeIndex.value = index;

    useMCreativeStore().setUrl();
    const { selectMMedium } = useMMediaStore();
    selectMMedium();
  };

  const isCreativeSelected = (index) => _selectedMCreativeIndex.value === index;

  const isNewCreative = (creative) => creative.id === null;
  const isRemovableCreative = (creative) => !creative.pushed;

  const updateCreativeStatus = (index, status) => (creatives.value[index].status = status);

  const creativeErrorMessages = (creative) => {
    const isImageWithoutTemplate =
      creative?.status === "active" && creative.media_type === "image" && !creative.template_ids?.length;
    return isImageWithoutTemplate ? ["Select a template"] : [];
  };

  const adSetVariables = ref([]);

  const loadAdSetVariables = async () => {
    if (adSetVariables.value.length) return true;

    const { response, data } = await useMyFetch("/m_creatives/ad_set_variables").json();
    if (response.value.ok) adSetVariables.value = data.value;
    return response.value.ok;
  };

  let defaultSubtitlesByLanguage = null;

  const loadDefaultSubtitlesByLanguage = () => {
    defaultSubtitlesByLanguage ??= useMyFetch("/m_creatives/default_subtitles")
      .json()
      .then(({ response, data }) => {
        if (response.value.ok) return data.value;

        defaultSubtitlesByLanguage = null;
        return {};
      });
    return defaultSubtitlesByLanguage;
  };

  const addCreative = async () => {
    const { response, data } = await useMyFetch(
      `/m_campaigns/${currentMCampaign.value.token}/m_creatives/new`,
    ).json();

    if (response.value.ok) {
      const previousCreative = creatives.value.at(-1);
      const newCreative = { ...data.value, name: `C${creatives.value.length + 1}` };
      if (previousCreative?.titles) newCreative.titles = [...previousCreative.titles];
      creatives.value.push(newCreative);
      selectCreative(creatives.value.length - 1);
    } else {
      handleError(data);
    }
  };

  const removeCreative = (index) => {
    if (creatives.value.length <= 1) return;

    creatives.value.splice(index, 1);
    if (index >= creatives.value.length) selectCreative(creatives.value.length - 1);
    else selectCreative(index);
  };

  const duplicateCreative = (index) => {
    const newCreative = deepCopy(creatives.value.at(index));
    newCreative.id = null;
    newCreative.token = genToken();
    newCreative.pushed = false;
    newCreative.m_media = [];
    newCreative.name = `${newCreative.name} Copy`;

    creatives.value.push(newCreative);
    selectCreative(creatives.value.length - 1);
  };

  return {
    selectedMCreative,
    creatives,
    initialCreatives,
    selectCreative,
    isCreativeSelected,
    addCreative,
    removeCreative,
    duplicateCreative,
    isNewCreative,
    isRemovableCreative,
    updateCreativeStatus,
    creativeErrorMessages,
    loadDefaultSubtitlesByLanguage,
    adSetVariables,
    loadAdSetVariables,
  };
});
